import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Loader } from '@liveconnect/communities-ui'
import {
  FormProvider,
  FormRichText,
  RadioControl,
} from '@liveconnect/components'

import { buildValidationSchema } from './validations'
import useNotifications from '../../../utils/notifications/useNotifications'
import FormActions from '../../../components/FormActions'
import usePrivacyPolicies from '../../../core/privacyPolicies/usePrivacyPolicies'
import useLocalizations from '../../../core/localizations/useLocalizations'
import { PrivacyPoliciesDetail } from '../../../core/privacyPolicies/types'
import useCulture from '../../../utils/culture'
import useUi from '../../../core/ui/useUi'
import HistoryModal from './HistoryModal'
import { DEFAULT_LANG } from '../../../i18n/config'
import { useBlockRouteChangeWithDialog } from '../../../utils/routing/useBlockRouteChange'

import './styles.scss'

interface FormType {
  value: string
}

const PrivacyPoliciesForm: FC = () => {
  const { t } = useTranslation()
  const notify = useNotifications()
  const { localizations } = useLocalizations()
  const { formatDate } = useCulture()
  const { showConfirmation } = useUi()
  const { current, fetchCurrent, createPrivacyPolicies } = usePrivacyPolicies()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANG)
  const [isEditting, setIsEditting] = useState<boolean>(false)
  const [currentForm, setCurrentForm] = useState<PrivacyPoliciesDetail>()
  const [showHistory, setShowHistory] = useState<boolean>(false)

  const currentValue = useMemo(
    () =>
      currentForm?.privacyPolicyValues.find(
        (item) => item.isoCode === selectedLang
      )?.value ?? '',
    [selectedLang, isEditting, currentForm]
  )

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: yupResolver(buildValidationSchema(t)),
    defaultValues: {
      value: currentValue,
    },
  })

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isValid, isDirty },
  } = methods

  useBlockRouteChangeWithDialog(isDirty && !isSubmitting)

  const handleChangeLang = (isoCode: string) => {
    if (isEditting && currentForm) {
      const langIndex = currentForm.privacyPolicyValues.findIndex(
        (item) => item.isoCode === selectedLang
      )
      if (langIndex !== -1) {
        const privacyPolicyValues = [...currentForm.privacyPolicyValues]
        privacyPolicyValues[langIndex] = {
          ...privacyPolicyValues[langIndex],
          value: getValues('value'),
        }
        setCurrentForm({ ...currentForm, privacyPolicyValues })
      }
    }
    setSelectedLang(isoCode)
  }

  const handleConfirmCancel = () => {
    if (isDirty) {
      showConfirmation({
        title: t('modal.cancel.generic.title'),
        subtitle: t('modal.cancel.generic.subtitle'),
        text: t('modal.cancel.generic.text'),
        confirmText: t('common.yes'),
        cancelText: t('common.no'),
        iconName: 'report_problem',
        onConfirm: () => {
          handleCancel()
        },
      })
    } else {
      handleCancel()
    }
  }

  const handleCancel = () => {
    setIsEditting(false)
    current && setCurrentForm(current)
    reset({
      value: currentValue,
    })
  }

  const handleSaveConfirmation = (): Promise<boolean> =>
    new Promise((resolve) => {
      if (isDirty) {
        showConfirmation({
          title: t('privacyPolicies.form.saveConfirmation.title'),
          subtitle: t('privacyPolicies.form.saveConfirmation.subtitle'),
          confirmText: t('common.save'),
          cancelText: t('common.cancel'),
          onConfirm: () => {
            resolve(true)
          },
          onCancel: () => {
            resolve(false)
          },
        })
      } else {
        resolve(true)
      }
    })

  const onSubmit = async ({ value }: FormType) => {
    setIsSubmitting(true)
    try {
      if (currentForm) {
        const langIndex = currentForm.privacyPolicyValues.findIndex(
          (item) => item.isoCode === selectedLang
        )
        if (langIndex !== -1) {
          const privacyPolicyValues = [...currentForm.privacyPolicyValues]
          privacyPolicyValues[langIndex] = {
            ...privacyPolicyValues[langIndex],
            value: value,
          }

          if (privacyPolicyValues.some((item) => !item.value)) {
            showConfirmation({
              title: t('common.warning'),
              subtitle: t('privacyPolicies.form.emptyConfirmation.subtitle'),
              text: t('privacyPolicies.form.emptyConfirmation.text'),
              hideCancel: true,
              confirmText: t('privacyPolicies.form.emptyConfirmation.confirm'),
            })
          } else {
            const continueSaving = await handleSaveConfirmation()
            if (continueSaving) {
              await createPrivacyPolicies(privacyPolicyValues)
              notify.success(t('privacyPolicies.form.successFeedback'))
              setIsEditting(false)
            }
          }
        }
      }
    } catch (e) {
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    reset({
      value: currentValue,
    })
  }, [selectedLang, currentForm])

  useEffect(() => {
    if (current) {
      reset({
        value: currentValue,
      })
      setCurrentForm(current)
    } else {
      setCurrentForm({
        id: '',
        code: '',
        creationDate: '',
        author: '',
        privacyPolicyValues: localizations.map((item) => ({
          isoCode: item.isoCode,
          value: '',
        })),
      })
    }
  }, [current, localizations])

  useEffect(() => {
    fetchCurrent()
  }, [])

  return (
    <>
      <div className="PrivacyPoliciesForm">
        <div className="d-flex justify-content-between">
          <div>
            <div className="PrivacyPoliciesForm__header-values d-flex">
              <div>
                <span>{t('privacyPolicies.code')}:</span> {current?.code}
              </div>
              <div>
                <span>{t('privacyPolicies.date')}:</span>{' '}
                {current?.creationDate &&
                  formatDate(
                    new Date(current?.creationDate as string) as Date,
                    'LONG_DATE_TIME'
                  )}
              </div>
              <div>
                <span>{t('privacyPolicies.author')}:</span> {current?.author}
              </div>
            </div>
          </div>
          <div className="TermsConsForm__langs d-flex">
            {localizations.map((item) => (
              <RadioControl
                key={item.isoCode}
                id=""
                name=""
                label={t(`localizations.${item.isoCode}`)}
                value={item.isoCode}
                checked={item.isoCode === selectedLang}
                onChange={() => handleChangeLang(item.isoCode)}
                hiddenLabel={false}
              />
            ))}
          </div>
        </div>
        <section>
          <FormProvider methods={methods}>
            <div className="row">
              <div className="col-12">
                {isEditting ? (
                  <FormRichText
                    key={selectedLang}
                    control={control}
                    name="value"
                    label=""
                    placeholder={t('privacyPolicies.form.value.placeholder')}
                  />
                ) : (
                  <div
                    className="PrivacyPoliciesForm__value-box"
                    dangerouslySetInnerHTML={{
                      __html:
                        currentValue ??
                        t('privacyPolicies.form.value.placeholder'),
                    }}
                  />
                )}
              </div>
            </div>
          </FormProvider>
        </section>
        <FormActions>
          {isEditting ? (
            <>
              <button
                className="btn btn-outline-primary"
                onClick={handleConfirmCancel}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {isSubmitting ? <Loader /> : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-outline-primary"
                onClick={() => setShowHistory(true)}
              >
                {t('privacyPolicies.form.changeHistory')}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setIsEditting(true)}
              >
                {t('common.edit')}
              </button>
            </>
          )}
        </FormActions>
      </div>
      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}
    </>
  )
}

export default PrivacyPoliciesForm
